import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { Container, PageHeader, Section, SettingRow } from '@vnetwork/ui/layout';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices, serviceLabel, serviceProviderScope } from '@/api/services';
import { useInstanceList } from '@/api/opennebula/instances/instance-reads';

/** Legacy scope of the Managed Kubernetes (Wolf) service that runs on compute. */
const KUBERNETES_SCOPE = 'Internal.Opennebula.Kubernetes';
import { ComputeRenameServiceDialog } from './ComputeRenameServiceDialog';
import { ComputeCancelSubscriptionDialog } from './ComputeCancelSubscriptionDialog';
import { ComputeDeleteServiceDialog } from './ComputeDeleteServiceDialog';

/**
 * Settings page for a scoped Compute (OpenNebula) console.
 * Exposes: rename (General), cancel subscription + delete service (Danger zone).
 * Danger-zone actions are blocked while instances exist.
 */
export function ComputeSettings() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [renameOpen, setRenameOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  const { data: instances = [], isLoading: instancesLoading } = useInstanceList(serviceId);

  const service = useMemo(
    () => services.find((s) => s.id === serviceId),
    [services, serviceId],
  );

  const serviceName = service ? serviceLabel(service) : '';
  const instanceCount = instances.length;
  const isLoading = servicesLoading || instancesLoading;

  // Managed Kubernetes runs on this compute — the org can't cancel/delete compute
  // while a k8s service is still active. Block on that dependency too (matches the
  // backend guard), taking priority over the instance count in the message.
  const hasKubernetes = useMemo(
    () => services.some((s) => serviceProviderScope(s) === KUBERNETES_SCOPE),
    [services],
  );
  const blocked = instanceCount > 0 || hasKubernetes;
  const blockMsg = hasKubernetes
    ? 'Cancel Managed Kubernetes first — it runs on this Compute service.'
    : `Blocked: ${instanceCount} instance${instanceCount === 1 ? '' : 's'} still running — delete them first.`;

  const nameControl = isLoading ? (
    <Skeleton style={{ height: 28, width: 160 }} />
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
      <Badge tone="neutral" appearance="subtle">{serviceName || '—'}</Badge>
      <Button variant="secondary" size="sm" disabled={!service} onClick={() => setRenameOpen(true)}>
        Rename
      </Button>
    </div>
  );

  const blockedAlert = blocked ? (
    <Alert
      tone="warning"
      title={hasKubernetes ? 'Managed Kubernetes is still active' : `Blocked: ${instanceCount} instance${instanceCount === 1 ? '' : 's'} still running`}
    >
      {blockMsg}
    </Alert>
  ) : null;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Settings size={22} />}
        title="Settings"
        description="Manage this Compute service."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Section
          title="General"
          description="Basic details for this Compute service."
          style={{ marginBottom: 0 }}
        >
          <SettingRow
            title="Service name"
            description="The display name shown across the console and service list."
            control={nameControl}
          />
        </Section>

        <Section
          title="Danger zone"
          description="Destructive actions for this Compute service."
          style={{ marginBottom: 0 }}
        >
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {blockedAlert}

          <SettingRow
            title="Cancel subscription"
            description={
              blocked ? blockMsg : 'Archives the billing subscription and marks this service as cancelled.'
            }
            control={
              <Button
                variant="danger"
                size="sm"
                disabled={!service || blocked}
                onClick={() => setCancelOpen(true)}
              >
                Cancel subscription
              </Button>
            }
          />

          <SettingRow
            title="Delete this service"
            description={
              blocked ? blockMsg : 'Removes the Compute service and its zone bindings. Recorded in the activity log.'
            }
            control={
              <Button
                variant="danger"
                size="sm"
                disabled={!service || blocked}
                onClick={() => setDeleteOpen(true)}
              >
                Delete service
              </Button>
            }
          />
          </div>
        </Section>
      </div>

      {service && (
        <>
          <ComputeRenameServiceDialog
            open={renameOpen}
            onOpenChange={setRenameOpen}
            serviceId={serviceId!}
            currentName={serviceName}
          />
          <ComputeCancelSubscriptionDialog
            open={cancelOpen}
            onOpenChange={setCancelOpen}
            serviceId={serviceId!}
            serviceName={serviceName}
          />
          <ComputeDeleteServiceDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            serviceId={serviceId!}
            serviceName={serviceName}
            instanceCount={instanceCount}
          />
        </>
      )}
    </Container>
  );
}
